import { Pagination } from "@mui/material";
import { MprcContainer } from "components";
import { MprcFilterCountInfo, MprcFilterLine } from "components/filter";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { MprcButtonDropdownChecked } from "components/MprcButtonDropdownChecked";
import { MprcSelectPeriod } from "components/MprcSelectPeriod";
import { GlobalSearchOrderBy, TrainingContentType, TrainingSearchDuration, TrainingSearchPublication, TrainingSearchResponse, TrainingSearchRestriction, TrainingSearchStart } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalSearchOrderByComponent } from "views/general/GlobalSearch/components/GlobalSearchOrderByComponent";
import { FullGlobalSearchButtonOptions, FullGlobalSearchOptionsLine, GlobalSearchRowsPerPage } from "views/general/GlobalSearch/components/GlobalSearchTabs";
import { SearchRenderTraining } from "views/general/GlobalSearch/components/SearchRenderTraining";
import { useGlobalSearch } from "views/general/GlobalSearch/GlobalSearchProvider";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const FullTrainingSearch = () => {
    const { t } = useTranslation("shared");
    const api = useCoreApi();

    const {
        searchResult,
        globalSearchText,
        loading, setLoading,
        pageNumber, setPageNumber,
        filterOpened, setFilterOpened,
        orderBy, setOrderBy,
        tFilters: filters, setTrainingFilters: setFilters,
        setErrors: setLastError
    } = useGlobalSearch();

    const [dialog, setDialog] = useState<any>(null);
    const [viewData, setViewData] = useState<TrainingSearchResponse | null>(searchResult?.training ?? null);

    ///
    const getCustomDate = (customStartDate: Date | null, customEndDate: Date | null, update: (ds: Date | null, de: Date | null) => void) => {
        setDialog(
            <MprcSelectPeriod
                startDate={customStartDate}
                endDate={customEndDate}
                onClose={
                    (period) => {
                        if (period) update(period.start, period.end);
                        setDialog(null);
                    }
                }
            />
        );
    };

    ///
    const renderFilter = useCallback(() => {
        return (
            <MprcFilterLine>
                <div>
                    <MprcButtonDropdownChecked
                        title={t("GlobalSearch.Filters.Training.ContentType")}
                        selected={filters.trainingContentType}
                        onChange={(selected) => setFilters({ ...filters, trainingContentType: selected })}
                        items={[
                            { text: t("lms:Student.Catalog.Commands.TypeAny"), value: null },
                            { text: t("lms:Student.Catalog.Commands.TypeCourseWeb"), value: TrainingContentType.Web },
                            { text: t("lms:Student.Catalog.Commands.TypedLivePresential"), value: TrainingContentType.LivePresential },
                            { text: t("lms:Student.Catalog.Commands.TypeLiveOnline"), value: TrainingContentType.LiveOnline },
                            { text: t("lms:Student.Catalog.Commands.TypeLiveMix"), value: TrainingContentType.LiveMix },
                            { text: t("lms:Student.Catalog.Commands.TypeAssessment"), value: TrainingContentType.Evaluation },
                            { text: t("lms:Student.Catalog.Commands.TypeActivity"), value: TrainingContentType.Activity },
                            { text: t("lms:Student.Catalog.Commands.TypeExternalCourse"), value: TrainingContentType.ExternalCourse },
                            { text: t("lms:Student.Catalog.Commands.TypeLearnPath"), value: TrainingContentType.LearnPath },
                            //{ text: t("lms:Student.Catalog.Commands.TypeGame"), value: TrainingContentType.Game },
                        ]}
                    />

                    <MprcButtonDropdownChecked
                        title={t("GlobalSearch.Filters.Training.Workload")}
                        selected={filters.duration}
                        onChange={(selected) => setFilters({ ...filters, duration: selected })}
                        items={[
                            { text: t("lms:Student.Catalog.Commands.WorkloadAny"), value: null },
                            { text: t("lms:Student.Catalog.Commands.Workload5min"), value: TrainingSearchDuration.Until5 },
                            { text: t("lms:Student.Catalog.Commands.Workload5To30Min"), value: TrainingSearchDuration.Between5And30 },
                            { text: t("lms:Student.Catalog.Commands.Workload30To60Min"), value: TrainingSearchDuration.Between30And60 },
                            { text: t("lms:Student.Catalog.Commands.Workload1To5Hours"), value: TrainingSearchDuration.Between60And300 },
                            { text: t("lms:Student.Catalog.Commands.Workload5To10Hours"), value: TrainingSearchDuration.Between300And600 },
                            { text: t("lms:Student.Catalog.Commands.WorkloadOther"), value: TrainingSearchDuration.Over600 },
                        ]}
                    />

                    <MprcButtonDropdownChecked
                        title={t("GlobalSearch.Filters.Training.Publication")}
                        selected={filters.publication}
                        onChange={
                            (selected) => {
                                if (selected == TrainingSearchPublication.Custom) {
                                    getCustomDate(filters.publicationStart
                                        , filters.publicationEnd
                                        , (ds, de) => ds != null && de != null && setFilters({ publication: selected, publicationStart: ds, publicationEnd: de })
                                    );
                                }
                                else setFilters({ publication: selected, publicationStart: null, publicationEnd: null });
                            }
                        }
                        items={[
                            { text: t("lms:Student.Catalog.Commands.PublicationAny"), value: null },
                            { text: t("lms:Student.Catalog.Commands.LastDay"), value: TrainingSearchPublication.Last24Hours },
                            { text: t("lms:Student.Catalog.Commands.LastWeek"), value: TrainingSearchPublication.LastWeek },
                            { text: t("lms:Student.Catalog.Commands.LastMonth"), value: TrainingSearchPublication.LastMonth },
                            { text: t("lms:Student.Catalog.Commands.LastYear"), value: TrainingSearchPublication.LastYear },
                            { text: t("lms:Student.Catalog.Commands.OverYear"), value: TrainingSearchPublication.OverYear },
                            { text: t("lms:Student.Catalog.Commands.PublicationPeriod"), value: TrainingSearchPublication.Custom },
                        ]}
                    />

                    <MprcButtonDropdownChecked
                        title={t("GlobalSearch.Filters.Training.Start")}
                        selected={filters.start}
                        onChange={
                            (selected) => {
                                if (selected == TrainingSearchStart.Custom) {
                                    getCustomDate(filters.trainingStart
                                        , filters.trainingEnd
                                        , (ds, de) => ds != null && de != null && setFilters({ start: selected, trainingStart: ds, trainingEnd: de })
                                    );
                                }
                                else setFilters({ start: selected, trainingStart: null, trainingEnd: null });
                            }
                        }
                        items={[
                            { text: t("lms:Student.Catalog.Commands.StartAny"), value: null },
                            { text: t("lms:Student.Catalog.Commands.StartNowTrainings"), value: TrainingSearchStart.Immediate },
                            { text: t("lms:Student.Catalog.Commands.StartNext7Days"), value: TrainingSearchStart.Next7 },
                            { text: t("lms:Student.Catalog.Commands.StartNext30Days"), value: TrainingSearchStart.Next30 },
                            { text: t("lms:Student.Catalog.Commands.StartOver30Days"), value: TrainingSearchStart.Over30 },
                            { text: t("lms:Student.Catalog.Commands.StartPeriod"), value: TrainingSearchStart.Custom },
                        ]}
                    />

                    <MprcButtonDropdownChecked
                        title={t("GlobalSearch.Filters.Training.RestrictionOption")}
                        selected={filters.restriction}
                        selectedColor="inherit"
                        onChange={(selected) => setFilters({ ...filters, restriction: selected })}
                        items={
                            [
                                { text: t("GlobalSearch.Filters.Training.Restriction.All"), value: TrainingSearchRestriction.All },
                                { text: t("GlobalSearch.Filters.Training.Restriction.Title"), value: TrainingSearchRestriction.Title },
                            ]
                        }
                    />
                </div>

                <GlobalSearchOrderByComponent
                    selectedOption={orderBy}
                    onChange={(orderBy) => setOrderBy(orderBy)}
                />

            </MprcFilterLine>
        );
    }, [filters, orderBy]);

    /////////////////////////////////////////////////////////////////////////////////////////////////

    ///
    async function load(pageNumber: number) {
        setLoading(true);
        setLastError(null);

        try {
            const response = await api.v1.globalSearch.trainingSearch({
                searchText: globalSearchText,
                filters,
                orderBy,
                pageNumber,
                pageSize: GlobalSearchRowsPerPage,
                relevanceEnabled: pageNumber > 1 && (viewData?.relevanceEnabled ?? false)
            });
            setViewData(response.data);
        }
        catch (e) {
            setLastError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const renderSearch = () => {
        if (loading || viewData == null) return null;

        return viewData.traningSearchResult.totalRows == 0
            ? <MprcFilterEmptyDataset message={t("GlobalSearch.FullSearch.NoTrainingsFound")} />
            : (
                <>
                    <MprcContainer>
                        <MprcFilterCountInfo
                            text={t("GlobalSearch.FullSearch.TrainingsFound", { count: viewData.traningSearchResult.totalRows })}
                        />
                    </MprcContainer>

                    {
                        viewData.traningSearchResult.items.map((v, index) => {
                            return <SearchRenderTraining
                                key={index}
                                data={v}
                            />;
                        })
                    }

                    {
                        viewData.traningSearchResult.totalPages > 1 &&
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={pageNumber}
                            count={viewData.traningSearchResult.totalPages}
                            onChange={(e, pn) => setPageNumber(pn)}
                            sx={{ "& ul": { justifyContent: "center" } }}
                        />
                    }
                </>
            );
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////

    const filtering = useMemo(() => {
        return Object.entries(filters).filter(([key, value]) => key != "searchOption" && key != "restriction" && value != null).length > 0;
    }, [filters]);

    /////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (pageNumber == 1 && !filtering && orderBy == GlobalSearchOrderBy.Relevance && searchResult?.training != null)
            setViewData(searchResult.training);
        else
            load(pageNumber);
    }, [filters, orderBy, pageNumber, globalSearchText]);

    useEffect(() => {
        if (pageNumber == 1 && !filtering && orderBy == GlobalSearchOrderBy.Relevance && searchResult?.training != null)
            setViewData(searchResult.training);
    }, [searchResult]);

    const [el, setEl] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setEl(document.getElementById("SearchTabFilterOptions") as HTMLElement);
    }, []);

    /////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <FullGlobalSearchOptionsLine
                filterOpened={filterOpened}
                filter={renderFilter()}
                el={el}
            />

            <FullGlobalSearchButtonOptions
                filtering={filtering}
                onFilterClick={() => setFilterOpened(!filterOpened)}
            />

            {
                !loading && renderSearch()
            }
        </>
    );
};
